/**
 * Navigation Configuration
 *
 * Centralized nav configuration for the 7-room product model:
 * - Command Center
 * - Donors
 * - Campaigns
 * - Stories
 * - Meetings
 * - Grants
 * - Configure
 *
 * Used by CockpitSidebar as the primary workspace navigation.
 *
 * THIS FILE IS THE SOLE AUTHORITY FOR NAVIGATION. Features must not add
 * navigation entries elsewhere. A second, authoritative-looking definition at
 * `src/components/shell2/WorkspaceNav.tsx` had no importers; PR #497 added its
 * "Assets" entry there, so Assets shipped with a working route and no doorway.
 * That file is deleted (2026-09-21). Every entry here is checked against the
 * registered routes by `src/test/contracts/navRouteContract.test.ts`.
 */

import {
  Building2,
  LayoutDashboard,
  FolderKanban,
  ListChecks,
  Megaphone,
  Users,
  Calendar,
  ClipboardCheck,
  Settings,
  Palette,
  BarChart3,
  Tags,
  Target,
  Layers,
  FlaskConical,
  BookOpen,
  FileEdit,
  FileText,
  Sparkles,
  Inbox,
  Mail,
  Plug,
  FileSpreadsheet,
  Images,
  ShieldCheck,
  Landmark,
  TrendingUp,
  HeartHandshake,
  DollarSign,
} from "lucide-react";
import React from "react";

import { flags } from "@/config/featureFlags";

// =============================================================================
// TYPES
// =============================================================================

export interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  /** Tooltip description */
  description?: string;
  /** Badge text (e.g., "Beta", "Labs") */
  badge?: string;
  /** If true, requires capability check */
  requiresCapabilityCheck?: boolean;
}

export interface NavSection {
  id: string;
  /** Section header label (e.g., "DONORS") */
  label: string;
  /** Items in this section */
  items: NavItem[];
  /** If true, section is collapsible */
  collapsible?: boolean;
  /** Default collapsed state */
  defaultCollapsed?: boolean;
  /** Visual grouping — sections in different groups get extra spacing */
  group?: "core" | "rhythm" | "system";
}

export interface NavConfig {
  /** Header slot items (Command Center) - always visible at top */
  header: NavItem[];
  /** Main sections (Donors, Campaigns, Stories, Meetings) */
  sections: NavSection[];
  /** Configure section (utility items) */
  configure: NavSection;
  /** Pinned items (always visible at bottom) */
  pinned: NavItem[];
}

// =============================================================================
// PORTFOLIO NAV ITEMS (Layout 2 — platform-level routes, no client scope)
// =============================================================================

/**
 * Top-level nav items shown in the Sidebar when in portfolio (non-scoped) mode.
 * These are platform-level routes accessible without a client slug.
 */
export const portfolioNavItems: NavItem[] = [
  {
    id: "actions",
    label: "Actions",
    icon: React.createElement(ListChecks, { className: "h-4 w-4" }),
    path: "/actions",
    description: "Platform-wide actions and prepared next moves",
  },
  {
    id: "portfolio-command-center",
    label: "Decision Cockpit",
    icon: React.createElement(LayoutDashboard, { className: "h-4 w-4" }),
    path: "/cockpit",
    description: "Portfolio overview across all clients",
  },
  {
    id: "project-board",
    label: "Projects",
    icon: React.createElement(FolderKanban, { className: "h-4 w-4" }),
    path: "/projects",
    description: "What Track15 is doing and how each project goes live",
  },
  {
    id: "clients",
    label: "Clients",
    icon: React.createElement(Building2, { className: "h-4 w-4" }),
    path: "/clients",
    description: "Browse and manage all client workspaces",
  },
];

// =============================================================================
// COCKPIT NAV CONFIGURATION
// =============================================================================

/**
 * Workspace navigation structured around the 7-room product model.
 * Rooms answer "where should I look?" by matching how fundraisers think
 * about their work — not by internal product architecture.
 */
export const cockpitNavConfig: NavConfig = {
  // Header slot — Pulse (per-client view, always visible at top)
  header: [
    {
      id: "command-center",
      label: "Pulse",
      icon: React.createElement(LayoutDashboard, { className: "h-4 w-4" }),
      path: "",
      description: "What needs my attention right now?",
    },
    {
      id: "agent-inbox",
      label: "Agent Inbox",
      icon: React.createElement(Inbox, { className: "h-4 w-4" }),
      path: "agent-inbox",
      description: "AI-generated recommendations and action drafts",
    },
  ],

  // Main sections — 4 rooms
  sections: [
    // ── DONORS (core) ──────────────────────────────────────
    {
      id: "donors",
      label: "DONORS",
      group: "core",
      collapsible: true,
      defaultCollapsed: false,
      items: [
        {
          id: "portfolio",
          label: "Portfolio",
          icon: React.createElement(Users, { className: "h-4 w-4" }),
          path: "major-donors",
          description:
            "Portfolio Intelligence — tiers, rising, recovery, coverage",
        },
        {
          id: "donor-workflow",
          label: "Donor Workflow",
          icon: React.createElement(ListChecks, { className: "h-4 w-4" }),
          path: "donor-workflow",
          description:
            "Guided donor loop — identify, understand, act, complete",
        },
        {
          id: "donor-lab",
          label: "Donor Lab",
          icon: React.createElement(FlaskConical, { className: "h-4 w-4" }),
          path: "donor-lab",
          description: "Insights and predictive signals",
        },
      ],
    },

    // ── CAMPAIGNS (core) ───────────────────────────────────
    {
      id: "campaigns",
      label: "CAMPAIGNS",
      group: "core",
      collapsible: true,
      defaultCollapsed: false,
      items: [
        {
          id: "annual-giving-overview",
          label: "Overview",
          icon: React.createElement(LayoutDashboard, { className: "h-4 w-4" }),
          path: "annual-giving",
          description: "Annual giving priorities and tools",
        },
        {
          id: "campaigns-list",
          label: "Campaigns",
          icon: React.createElement(Megaphone, { className: "h-4 w-4" }),
          path: "campaigns",
          description: "Plan and manage fundraising efforts",
        },
        {
          id: "messaging",
          label: "Messaging",
          icon: React.createElement(Layers, { className: "h-4 w-4" }),
          path: "strategy",
          description: "Annual messaging and narrative planning",
        },
        {
          id: "events",
          label: "Events",
          icon: React.createElement(Calendar, { className: "h-4 w-4" }),
          path: "events",
          description: "Event planning, sponsorships, and follow-up",
        },
        {
          id: "planning-cycle",
          label: "Planning Cycle",
          icon: React.createElement(Target, { className: "h-4 w-4" }),
          path: "cycle",
          description: "Quarterly and seasonal planning",
        },
        {
          id: "email",
          label: "Email",
          icon: React.createElement(Mail, { className: "h-4 w-4" }),
          path: "email",
          description: "Email rules, templates, and sequences",
        },
        {
          id: "email-program",
          label: "Email Program",
          icon: React.createElement(Mail, { className: "h-4 w-4" }),
          path: "email-program",
          description: "Existing Constant Contact campaigns, lists & segments",
        },
        {
          id: "ask-ladders",
          label: "Ask Ladders",
          icon: React.createElement(DollarSign, { className: "h-4 w-4" }),
          path: "ask-ladders",
          description: "Track15 standard ask ladder, or this client's own",
        },
        ...(flags.enableDirectMailStudio
          ? [
              {
                id: "direct-mail",
                label: "Direct Mail",
                icon: React.createElement(FileText, { className: "h-4 w-4" }),
                path: "direct-mail",
                description: "Create and manage direct mail pieces",
              },
            ]
          : []),
      ],
    },

    // ── STORIES (core) ─────────────────────────────────────
    {
      id: "stories",
      label: "STORIES",
      group: "core",
      collapsible: true,
      defaultCollapsed: false,
      items: [
        {
          id: "story-bank",
          label: "Story Bank",
          icon: React.createElement(BookOpen, { className: "h-4 w-4" }),
          path: "story-bank",
          description: "Published stories and narrative assets",
        },
        {
          id: "worksheets",
          label: "Worksheets",
          icon: React.createElement(FileEdit, { className: "h-4 w-4" }),
          path: "story-engine",
          description: "Story capture worksheets in progress",
        },
        {
          id: "story-opportunities",
          label: "Opportunities",
          icon: React.createElement(Sparkles, { className: "h-4 w-4" }),
          path: "story-opportunities",
          description: "Story opportunity calendar and pipeline",
        },
      ],
    },

    // ── MEETINGS (rhythm) ──────────────────────────────────
    {
      id: "meetings",
      label: "MEETINGS",
      group: "rhythm",
      collapsible: true,
      defaultCollapsed: false,
      items: [
        {
          id: "meeting-prep",
          label: "Meeting Prep",
          icon: React.createElement(ClipboardCheck, { className: "h-4 w-4" }),
          path: "meetings",
          description: "Upcoming meetings and preparation status",
        },
      ],
    },

    // ── DELIVERABLES (rhythm) ──────────────────────────────
    {
      id: "deliverables",
      label: "DELIVERABLES",
      group: "rhythm",
      collapsible: true,
      defaultCollapsed: false,
      items: [
        {
          id: "deliverables-home",
          label: "Documents",
          icon: React.createElement(FileSpreadsheet, { className: "h-4 w-4" }),
          path: "deliverables",
          description: "Generate client-facing reports and analysis documents",
        },
        {
          id: "reactivation",
          label: "Reactivation Opportunity",
          icon: React.createElement(TrendingUp, { className: "h-4 w-4" }),
          path: "reactivation",
          description: "Ranked donor reactivation opportunities",
        },
        {
          id: "stewardship-engine",
          label: "Stewardship",
          icon: React.createElement(HeartHandshake, { className: "h-4 w-4" }),
          path: "stewardship",
          description: "Gift-based stewardship opportunities and touches",
        },
        {
          id: "client-assets",
          label: "Assets",
          icon: React.createElement(Images, { className: "h-4 w-4" }),
          path: "assets",
          description: "Marketing artwork with public URLs to paste anywhere",
        },
        {
          id: "context-check",
          label: "Context Check",
          icon: React.createElement(ShieldCheck, { className: "h-4 w-4" }),
          path: "context-check",
          description: "Check a draft against current client context",
        },
      ],
    },

    // ── GRANTS (core) ─────────────────────────────────────
    {
      id: "grants",
      label: "GRANTS",
      group: "core",
      collapsible: true,
      defaultCollapsed: false,
      items: [
        {
          id: "funder-intelligence",
          label: "Funder Intelligence",
          icon: React.createElement(Landmark, { className: "h-4 w-4" }),
          path: "funders",
          description:
            "Global funder directory — priorities, patterns, relationships",
        },
        {
          id: "grant-pipeline",
          label: "Pipeline",
          icon: React.createElement(Layers, { className: "h-4 w-4" }),
          path: "grants",
          description: "Grant lifecycle — acquisition and management phases",
        },
        {
          id: "program-evidence",
          label: "Program Evidence",
          icon: React.createElement(FileSpreadsheet, { className: "h-4 w-4" }),
          path: "evidence",
          description:
            "Upload and reference program data — evidence for grants",
        },
        {
          id: "grant-timeline",
          label: "Timeline",
          icon: React.createElement(Calendar, { className: "h-4 w-4" }),
          path: "grant-timeline",
          description: "Grant deadlines and reporting schedule",
        },
      ],
    },
  ],

  // Configure section — system room (collapsed by default)
  configure: {
    id: "configure",
    label: "CONFIGURE",
    collapsible: true,
    defaultCollapsed: true,
    group: "system",
    items: [
      {
        id: "settings",
        label: "Settings",
        icon: React.createElement(Settings, { className: "h-4 w-4" }),
        path: "settings",
        description: "Client profile, data, and admin controls",
      },
      {
        id: "integrations",
        label: "Integrations",
        icon: React.createElement(Plug, { className: "h-4 w-4" }),
        path: "settings?tab=integrations",
        description: "Connect email, project management, and social tools",
      },
      {
        id: "brand-profile",
        label: "Brand Profile",
        icon: React.createElement(Palette, { className: "h-4 w-4" }),
        path: "brand-profile",
        description: "Visual identity and messaging",
      },
      {
        id: "track15-analytics",
        label: "Content Quality",
        icon: React.createElement(BarChart3, { className: "h-4 w-4" }),
        path: "track15-analytics",
        description: "Track15 style compliance metrics",
      },
      {
        id: "normalization",
        label: "Normalization",
        icon: React.createElement(Tags, { className: "h-4 w-4" }),
        path: "normalization",
        description: "Map raw labels to canonical taxonomy",
      },
      {
        id: "assessment",
        label: "Assessment",
        icon: React.createElement(ClipboardCheck, { className: "h-4 w-4" }),
        path: "assessment",
        description: "Evaluate health and identify focus areas",
      },
    ],
  },

  // Pinned items — cross-client Track15 surfaces (absolute paths, not workspace-scoped)
  // "Track15 · Pulse" (-> /, the Clients doorway) removed 2026-09-22: this
  // section holds cross-client WORK surfaces only (track15_pulse_pinned_removal).
  pinned: [
    {
      id: "track15-inbox",
      label: "Track15 · Inbox",
      icon: React.createElement(Inbox, { className: "h-4 w-4" }),
      path: "/actions",
      description: "Platform-wide actions and prepared next moves",
    },
    {
      id: "track15-cockpit",
      label: "Track15 · Cockpit",
      icon: React.createElement(LayoutDashboard, { className: "h-4 w-4" }),
      path: "/cockpit",
      description: "Decision cockpit — cross-client overview",
    },
  ],
};

// =============================================================================
// STORAGE KEY FOR COLLAPSED STATE
// =============================================================================

export const NAV_COLLAPSED_STATE_KEY = "nexus:nav:collapsed";

/**
 * Get persisted collapsed state from localStorage.
 */
export function getPersistedCollapsedState(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(NAV_COLLAPSED_STATE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/**
 * Persist collapsed state to localStorage.
 */
export function persistCollapsedState(state: Record<string, boolean>): void {
  try {
    localStorage.setItem(NAV_COLLAPSED_STATE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}
